export const API_BASE = (import.meta.env['VITE_API_URL'] as string | undefined)?.replace(/\/$/, '') ?? '';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId: string | null = null,
    public readonly details: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  formData?: FormData;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Sent as Idempotency-Key, which money routes need: one per action, the same on a resend (`useIdempotencyKey`). */
  idempotencyKey?: string;
}

let tokenProvider: () => Promise<string | null> | string | null = () => null;
let gymIdProvider: () => string | null = () => null;
let gymRoleProvider: () => string | null = () => null;
let viewAsProvider: () => { role: string; memberId: string | null } | null = () => null;
let locationIdProvider: () => string | null = () => null;

export function configureApi(options: { getToken: typeof tokenProvider; getGymId: typeof gymIdProvider; getGymRole: typeof gymRoleProvider; getViewAs: typeof viewAsProvider }) {
  tokenProvider = options.getToken;
  gymIdProvider = options.getGymId;
  gymRoleProvider = options.getGymRole;
  viewAsProvider = options.getViewAs;
}

export function setLocationIdProvider(provider: () => string | null) {
  locationIdProvider = provider;
}

export function gymHost(): string {
  return window.location.host;
}

async function send(path: string, options: ApiRequestOptions): Promise<Response> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (options.query) {
    for (const [k, v] of Object.entries(options.query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
    }
  }
  const token = await tokenProvider();
  const gymId = gymIdProvider();
  const headers: Record<string, string> = { 'x-gym-host': gymHost() };
  if (token) headers['authorization'] = `Bearer ${token}`;
  if (gymId) headers['x-gym-id'] = gymId;
  const gymRole = gymRoleProvider();
  if (gymRole) headers['x-gym-role'] = gymRole;
  const viewAs = viewAsProvider();
  if (viewAs) headers['x-view-as'] = viewAs.role;
  if (viewAs?.memberId) headers['x-view-as-member'] = viewAs.memberId;
  const locationId = locationIdProvider();
  if (locationId) headers['x-location-id'] = locationId;
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;
  return fetch(url.toString(), {
    method: options.method ?? (options.body !== undefined || options.formData ? 'POST' : 'GET'),
    headers,
    body: options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
    signal: options.signal,
    credentials: 'omit',
  });
}

function parse(text: string): unknown {
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text;
  }
}

function errorFrom(res: Response, data: unknown): ApiError {
  const err = (data as { error?: { code?: string; message?: string; requestId?: string; details?: unknown } } | null)?.error;
  return new ApiError(res.status, err?.code ?? 'HTTP_ERROR', err?.message ?? `Request failed (${res.status})`, err?.requestId ?? null, err?.details ?? null);
}

export async function api<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const res = await send(path, options);
  const data = parse(await res.text());
  if (!res.ok) throw errorFrom(res, data);
  return data as T;
}

export async function apiBlob(path: string, options: ApiRequestOptions = {}): Promise<Blob> {
  const res = await send(path, options);
  if (!res.ok) throw errorFrom(res, parse(await res.text()));
  return res.blob();
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface KeysetPage<T> {
  items: T[];
  total: number | null;
  page: number;
  pageSize: number;
  nextCursor: string | null;
}
